"use client";

import { useEffect, useRef, useState } from "react";
import { useLang } from "@/contexts/language-context";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Menu, X, ArrowRight, ChevronDown, ChevronRight, Truck, Sparkles, Utensils, Moon } from "lucide-react";
import { getFeaturesByVertical } from "@/lib/marketing/features-data";

interface MarketingNavProps {
  isHome?: boolean;
}

type Dropdown = "solutions" | "features" | null;
type MobileSection = "solutions" | "features" | null;

export default function MarketingNav({ isHome = false }: MarketingNavProps) {
  const { lang, toggleLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState<Dropdown>(null);
  const [mobileSection, setMobileSection] = useState<MobileSection>(null);
  const router = useRouter();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const restaurantFeatures = getFeaturesByVertical("restaurant");
  const nightlifeFeatures = getFeaturesByVertical("nightlife");

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdown]);

  // Close dropdown on Escape
  useEffect(() => {
    if (!dropdown) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDropdown(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [dropdown]);

  const handleScroll = (id: string) => {
    setOpen(false);
    if (isHome) {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      router.push(`/#${id}`);
    }
  };

  const handleGetStarted = () => {
    setOpen(false);
    router.push("/signup");
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] }}
        className="fixed left-0 right-0 z-50 bg-[#FAFAFA]/90 backdrop-blur-xl border-b border-black/5"
        style={{ top: "var(--banner-height, 0px)" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between gap-6" style={{ height: "80px" }}>

          {/* Logo */}
          {isHome ? (
            <button onClick={() => handleScroll("hero")} className="flex items-center shrink-0">
              <img src="/redy-logo.png" alt="Redy" className="h-8 w-auto" />
            </button>
          ) : (
            <Link href="/" className="flex items-center shrink-0">
              <img src="/redy-logo.png" alt="Redy" className="h-8 w-auto" />
            </Link>
          )}

          {/* Desktop nav pill */}
          <div
            ref={dropdownRef}
            className="hidden lg:flex items-center bg-white border border-black/5 px-3 py-2 rounded-full shadow-sm gap-1"
          >
            {/* Solutions dropdown */}
            <button
              onClick={() => setDropdown(dropdown === "solutions" ? null : "solutions")}
              onMouseEnter={() => setDropdown("solutions")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-bold transition-colors cursor-pointer ${
                dropdown === "solutions"
                  ? "bg-[#FAFAFA] text-redy-charcoal"
                  : "text-redy-charcoal/55 hover:text-redy-charcoal"
              }`}
            >
              {t("Solutions", "Soluciones")}
              <ChevronDown className={`w-3 h-3 transition-transform ${dropdown === "solutions" ? "rotate-180" : ""}`} />
            </button>

            {/* Features dropdown */}
            <button
              onClick={() => setDropdown(dropdown === "features" ? null : "features")}
              onMouseEnter={() => setDropdown("features")}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-bold transition-colors cursor-pointer ${
                dropdown === "features"
                  ? "bg-[#FAFAFA] text-redy-charcoal"
                  : "text-redy-charcoal/55 hover:text-redy-charcoal"
              }`}
            >
              {t("Features", "Features")}
              <ChevronDown className={`w-3 h-3 transition-transform ${dropdown === "features" ? "rotate-180" : ""}`} />
            </button>

            {/* Pricing */}
            <Link
              href="/pricing"
              onMouseEnter={() => setDropdown(null)}
              className="px-4 py-1.5 rounded-full text-[13px] font-bold text-redy-charcoal/55 hover:text-redy-charcoal transition-colors"
            >
              {t("Pricing", "Precios")}
            </Link>

            {/* Hardware */}
            <Link
              href="/hardware"
              onMouseEnter={() => setDropdown(null)}
              className="px-4 py-1.5 rounded-full text-[13px] font-bold text-redy-charcoal/55 hover:text-redy-charcoal transition-colors"
            >
              {t("Hardware", "Hardware")}
            </Link>

            {/* Capital */}
            <Link
              href="/capital"
              onMouseEnter={() => setDropdown(null)}
              className="px-4 py-1.5 rounded-full text-[13px] font-bold text-redy-charcoal/55 hover:text-redy-charcoal transition-colors"
            >
              {t("Capital", "Capital")}
            </Link>

            {/* Blog */}
            <Link
              href="/blog"
              onMouseEnter={() => setDropdown(null)}
              className="px-4 py-1.5 rounded-full text-[13px] font-bold text-redy-charcoal/55 hover:text-redy-charcoal transition-colors"
            >
              {t("Blog", "Blog")}
            </Link>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="text-[12px] font-black text-redy-charcoal px-3 py-2 rounded-full border border-black/5 bg-white shadow-sm hover:bg-[#F0EDE8] transition-all cursor-pointer"
            >
              {lang === "en" ? "ES" : "EN"}
            </button>
            <Link
              href="/login"
              className="inline-flex items-center text-[13px] font-bold text-redy-charcoal px-5 py-2.5 rounded-full border border-black/10 bg-white shadow-sm hover:bg-[#F0EDE8] transition-all"
            >
              {t("Log in", "Iniciar sesión")}
            </Link>
            <button
              onClick={handleGetStarted}
              className="text-[14px] font-bold text-white bg-redy-charcoal px-6 py-2.5 rounded-full hover:bg-black transition-all active:scale-95 shadow-sm cursor-pointer"
            >
              {t("Get started", "Comenzar")}
            </button>
          </div>

          {/* Mobile right side */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={toggleLang}
              className="text-[11px] font-black text-redy-charcoal px-2.5 py-1.5 rounded-full border border-black/5 bg-white shadow-sm cursor-pointer"
            >
              {lang === "en" ? "ES" : "EN"}
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-black/5 shadow-sm cursor-pointer"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* ── Desktop dropdown panels ─────────────────────────────────────── */}
        <AnimatePresence>
          {dropdown && (
            <motion.div
              key={dropdown}
              // x:"-50%" lives inside motion's transform (alongside `y`) so
              // it survives framer-motion's transform reset. Combined with
              // `left-1/2`, this centers the dropdown horizontally inside
              // the fixed nav. Do NOT add `-translate-x-1/2` to className —
              // motion's inline transform will override it and the panel
              // will drift to the right.
              initial={{ opacity: 0, y: -8, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              exit={{ opacity: 0, y: -8, x: "-50%" }}
              transition={{ duration: 0.2 }}
              onMouseLeave={() => setDropdown(null)}
              className="hidden lg:block absolute left-1/2 top-[76px] w-full max-w-[min(1100px,calc(100vw-32px))] px-6"
            >
              <div className="bg-white border border-black/5 rounded-[24px] shadow-2xl overflow-hidden">
                {dropdown === "solutions" && (
                  <div className="grid grid-cols-3 divide-x divide-black/5">
                    <Link
                      href="/solution/restaurants"
                      onClick={() => setDropdown(null)}
                      className="group p-6 hover:bg-[#FAFAFA] transition-colors"
                    >
                      <div className="w-11 h-11 bg-redy-orange/10 rounded-[14px] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Utensils className="w-5 h-5 text-redy-orange" />
                      </div>
                      <div className="text-[15px] font-black text-redy-charcoal mb-1.5">
                        {t("Restaurants (Dine-in)", "Restaurantes (Dine-in)")}
                      </div>
                      <div className="text-[13px] font-medium text-redy-secondary leading-relaxed">
                        {t(
                          "Floor map, tableside ordering, splits, multi-station KDS, reservations — built for full-service.",
                          "Floor map, orden tableside, splits, KDS multi-estación, reservas — para servicio completo."
                        )}
                      </div>
                      <div className="inline-flex items-center gap-1 text-[12px] font-bold text-redy-orange mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        {t("Explore", "Explorar")} <ChevronRight className="w-3 h-3" />
                      </div>
                    </Link>
                    <Link
                      href="/solution/food-trucks-qsr"
                      onClick={() => setDropdown(null)}
                      className="group p-6 hover:bg-[#FAFAFA] transition-colors"
                    >
                      <div className="w-11 h-11 bg-redy-red/10 rounded-[14px] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Truck className="w-5 h-5 text-redy-red" />
                      </div>
                      <div className="text-[15px] font-black text-redy-charcoal mb-1.5">
                        {t("Food Trucks & QSR", "Food Trucks y QSR")}
                      </div>
                      <div className="text-[13px] font-medium text-redy-secondary leading-relaxed">
                        {t(
                          "Counter-service POS with dual pricing, offline cash mode, SMS pickup alerts and self-order kiosk.",
                          "POS de mostrador con dual pricing, modo efectivo offline, alertas SMS y kiosko de auto-orden."
                        )}
                      </div>
                      <div className="inline-flex items-center gap-1 text-[12px] font-bold text-redy-red mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        {t("Explore", "Explorar")} <ChevronRight className="w-3 h-3" />
                      </div>
                    </Link>
                    <Link
                      href="/solution/nightlife"
                      onClick={() => setDropdown(null)}
                      className="group p-6 hover:bg-[#FAFAFA] transition-colors"
                    >
                      <div className="w-11 h-11 rounded-[14px] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: "rgba(124,58,237,0.15)" }}
                      >
                        <Sparkles className="w-5 h-5" style={{ color: "#7C3AED" }} />
                      </div>
                      <div className="text-[15px] font-black text-redy-charcoal mb-1.5">
                        {t("Bars, Clubs & Live Events", "Bares, Clubs y Eventos")}
                      </div>
                      <div className="text-[13px] font-medium text-redy-secondary leading-relaxed">
                        {t(
                          "Event ticketing, QR door, bar tabs, VIP map, promoter links — one platform for the whole venue.",
                          "Ticketing, puerta QR, tabs de barra, mapa VIP, links de promotores — una plataforma para todo el venue."
                        )}
                      </div>
                      <div className="inline-flex items-center gap-1 text-[12px] font-bold mt-3 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: "#7C3AED" }}
                      >
                        {t("Explore", "Explorar")} <ChevronRight className="w-3 h-3" />
                      </div>
                    </Link>
                  </div>
                )}

                {dropdown === "features" && (
                  <>
                    <div className="grid grid-cols-2 divide-x divide-black/5">
                      {/* Restaurant features */}
                      <div className="p-7">
                        <div className="flex items-center gap-2 mb-5">
                          <Utensils className="w-3.5 h-3.5 text-redy-orange" />
                          <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal">
                            {t("Restaurant", "Restaurant")}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          {restaurantFeatures.map((f) => {
                            const Icon = f.icon;
                            return (
                              <Link
                                key={f.slug}
                                href={`/features/${f.slug}`}
                                onClick={() => setDropdown(null)}
                                className="group flex items-center gap-2.5 p-2.5 rounded-[12px] hover:bg-[#FAFAFA] transition-colors"
                              >
                                <div className="w-8 h-8 bg-[#FAFAFA] rounded-[10px] flex items-center justify-center shrink-0 group-hover:bg-white group-hover:shadow-sm transition-all">
                                  <Icon className="w-3.5 h-3.5 text-redy-charcoal/70" />
                                </div>
                                <span className="text-[12.5px] font-bold text-redy-charcoal/80 group-hover:text-redy-charcoal transition-colors leading-snug">
                                  {t(f.card.title.en, f.card.title.es)}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>

                      {/* Nightlife features */}
                      <div className="p-7 bg-[#FAFAFA]/40">
                        <div className="flex items-center gap-2 mb-5">
                          <Moon className="w-3.5 h-3.5" style={{ color: "#7C3AED" }} />
                          <div className="text-[11px] font-black tracking-widest uppercase text-redy-charcoal">
                            {t("Nightlife", "Nightlife")}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                          {nightlifeFeatures.map((f) => {
                            const Icon = f.icon;
                            const accent = f.accent ?? "#7C3AED";
                            return (
                              <Link
                                key={f.slug}
                                href={`/features/${f.slug}`}
                                onClick={() => setDropdown(null)}
                                className="group flex items-center gap-2.5 p-2.5 rounded-[12px] hover:bg-white transition-colors"
                              >
                                <div
                                  className="w-8 h-8 rounded-[10px] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"
                                  style={{ backgroundColor: `${accent}22` }}
                                >
                                  <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
                                </div>
                                <span className="text-[12.5px] font-bold text-redy-charcoal/80 group-hover:text-redy-charcoal transition-colors leading-snug">
                                  {t(f.card.title.en, f.card.title.es)}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-black/5 px-7 py-4 flex items-center justify-between bg-white">
                      <span className="text-[12px] font-medium text-redy-secondary">
                        {t("18 features. One platform. $0.", "18 features. Una plataforma. $0.")}
                      </span>
                      <Link
                        href="/features"
                        onClick={() => setDropdown(null)}
                        className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-charcoal hover:text-black transition-colors"
                      >
                        {t("View all features", "Ver todas las features")}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* ── Mobile drawer ──────────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
              onClick={() => setOpen(false)}
            />

            <motion.div
              key="drawer"
              initial={{ opacity: 0, y: -24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="fixed top-[80px] left-4 right-4 z-50 lg:hidden bg-white rounded-[28px] shadow-2xl border border-black/5 overflow-hidden max-h-[calc(100vh-100px)] overflow-y-auto"
            >
              {/* Solutions section */}
              <div className="px-4 pt-4">
                <button
                  onClick={() => setMobileSection(mobileSection === "solutions" ? null : "solutions")}
                  className="w-full flex items-center justify-between px-4 py-3.5 rounded-[16px] text-[16px] font-bold text-redy-charcoal hover:bg-[#FAFAFA] active:bg-[#F0EDE8] transition-colors cursor-pointer"
                >
                  {t("Solutions", "Soluciones")}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      mobileSection === "solutions" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {mobileSection === "solutions" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 pr-2 pb-2 space-y-1">
                        <Link
                          href="/solution/restaurants"
                          onClick={() => setOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-[14px] text-[14px] font-bold text-redy-charcoal/80 hover:bg-[#FAFAFA] transition-colors"
                        >
                          <Utensils className="w-4 h-4 text-redy-orange" />
                          {t("Restaurants (Dine-in)", "Restaurantes (Dine-in)")}
                        </Link>
                        <Link
                          href="/solution/food-trucks-qsr"
                          onClick={() => setOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-[14px] text-[14px] font-bold text-redy-charcoal/80 hover:bg-[#FAFAFA] transition-colors"
                        >
                          <Truck className="w-4 h-4 text-redy-red" />
                          {t("Food Trucks & QSR", "Food Trucks y QSR")}
                        </Link>
                        <Link
                          href="/solution/nightlife"
                          onClick={() => setOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-[14px] text-[14px] font-bold text-redy-charcoal/80 hover:bg-[#FAFAFA] transition-colors"
                        >
                          <Sparkles className="w-4 h-4" style={{ color: "#7C3AED" }} />
                          {t("Bars, Clubs & Events", "Bares, Clubs y Eventos")}
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Features section */}
                <button
                  onClick={() => setMobileSection(mobileSection === "features" ? null : "features")}
                  className="w-full flex items-center justify-between px-4 py-3.5 rounded-[16px] text-[16px] font-bold text-redy-charcoal hover:bg-[#FAFAFA] active:bg-[#F0EDE8] transition-colors cursor-pointer"
                >
                  {t("Features", "Features")}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      mobileSection === "features" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {mobileSection === "features" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 pr-2 pb-3 space-y-0.5">
                        <div className="px-4 pt-2 pb-1 text-[10px] font-black tracking-widest uppercase text-redy-orange flex items-center gap-2">
                          <Utensils className="w-3 h-3" />
                          {t("Restaurant", "Restaurant")}
                        </div>
                        {restaurantFeatures.map((f) => {
                          const Icon = f.icon;
                          return (
                            <Link
                              key={f.slug}
                              href={`/features/${f.slug}`}
                              onClick={() => setOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 rounded-[12px] text-[13.5px] font-bold text-redy-charcoal/75 hover:bg-[#FAFAFA] transition-colors"
                            >
                              <Icon className="w-3.5 h-3.5 text-redy-charcoal/50 shrink-0" />
                              {t(f.card.title.en, f.card.title.es)}
                            </Link>
                          );
                        })}
                        <div className="px-4 pt-3 pb-1 text-[10px] font-black tracking-widest uppercase flex items-center gap-2" style={{ color: "#7C3AED" }}>
                          <Moon className="w-3 h-3" />
                          {t("Nightlife", "Nightlife")}
                        </div>
                        {nightlifeFeatures.map((f) => {
                          const Icon = f.icon;
                          const accent = f.accent ?? "#7C3AED";
                          return (
                            <Link
                              key={f.slug}
                              href={`/features/${f.slug}`}
                              onClick={() => setOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 rounded-[12px] text-[13.5px] font-bold text-redy-charcoal/75 hover:bg-[#FAFAFA] transition-colors"
                            >
                              <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
                              {t(f.card.title.en, f.card.title.es)}
                            </Link>
                          );
                        })}
                        <Link
                          href="/features"
                          onClick={() => setOpen(false)}
                          className="flex items-center justify-between mt-2 px-4 py-3 rounded-[12px] text-[13px] font-black text-redy-charcoal bg-[#FAFAFA] hover:bg-[#F0EDE8] transition-colors"
                        >
                          {t("View all 18 features", "Ver las 18 features")}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <Link
                  href="/pricing"
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3.5 rounded-[16px] text-[16px] font-bold text-redy-charcoal hover:bg-[#FAFAFA] active:bg-[#F0EDE8] transition-colors"
                >
                  {t("Pricing", "Precios")}
                </Link>

                <Link
                  href="/hardware"
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3.5 rounded-[16px] text-[16px] font-bold text-redy-charcoal hover:bg-[#FAFAFA] active:bg-[#F0EDE8] transition-colors"
                >
                  {t("Hardware", "Hardware")}
                </Link>

                <Link
                  href="/blog"
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3.5 rounded-[16px] text-[16px] font-bold text-redy-charcoal hover:bg-[#FAFAFA] active:bg-[#F0EDE8] transition-colors"
                >
                  {t("Blog", "Blog")}
                </Link>
              </div>

              <div className="mx-6 border-t border-black/5 mt-2" />

              <div className="px-6 py-5 flex flex-col gap-3">
                <Link
                  href="/login"
                  onClick={() => setOpen(false)}
                  className="w-full text-center py-3.5 rounded-[16px] text-[15px] font-bold text-redy-charcoal border border-black/10 hover:bg-[#FAFAFA] transition-colors"
                >
                  {t("Log in", "Iniciar sesión")}
                </Link>
                <button
                  onClick={handleGetStarted}
                  className="w-full py-3.5 rounded-[16px] text-[15px] font-bold text-white bg-redy-charcoal flex items-center justify-center gap-2 hover:bg-black transition-colors active:scale-95 cursor-pointer"
                >
                  {t("Get started free", "Comenzar gratis")}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
